import { useTranslation } from 'react-i18next';
import DashboardBlock from '../DashboardBlock/DashboardBlock';
import styles from './ApplicationsBarChart.module.scss';
import { useToastError } from '~/utils/useToastError';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useEffect, useState } from 'react';
import { Button } from '~/ui';
import SpinnerCircle from '~/ui/SpinnerCircle/SpinnerCircle';
import { NextPrevChartNames } from '~/utils/types/dashboard';
import { GeneralApplicationBar } from './GeneralApplicationBar/GeneralApplicationBar';
import { CurrentApplicationBar } from './CurrentApplicationBar/CurrentApplicationBar';
import { NextPrevDataLoadBtn } from '~/ui/NextPrevChartBtn/NextPrevDataLoadBtn';

export interface IOrderBarProps {
  all_count: number;
  total_pages: number;
  statistic: {
    [key: string]: {
      all_count: number;
      statuses: {
        created: number;
        in_progress: number;
        completed: number;
        closed: number;
        expired: number;
      };
    };
  };
}

export interface ICurrentApplicationData {
  label: string;
  data: {
    all_count: number;
    statistic: {
      created: number;
      in_progress: number;
      completed: number;
      closed: number;
      expired: number;
    };
  };
}

interface IApplicationsDataProps {
  startDate: string;
  endDate: string;
  assetsIds: [] | number[];
  accommodation_ids: number[];
  organization_ids: number[];
}

const ApplicationsBarChart = ({
  startDate,
  endDate,
  assetsIds,
  accommodation_ids,
  organization_ids,
}: IApplicationsDataProps) => {
  const [selectedData, setSelectedData] = useState<null | string>(null);
  const [currentData, setCurrentData] = useState<ICurrentApplicationData[]>([]);
  const [isCurrentData, setIsCurrentData] = useState(false);
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const toastError = useToastError();

  const handleReset = () => {
    setSelectedData(null);
    setCurrentData([]);
    setIsCurrentData(false);
    setPage(1);
  };

  const params = {
    time: [startDate, endDate],
    asset_ids: assetsIds,
    accommodation_ids,
    organization_ids,
    page,
    per_page: 5,
  };

  const { data: ticketData, isLoading } = useQuery<IOrderBarProps>({
    queryFn: async () => {
      const { data } = await instance.get('admin/dashboard/tickets', {
        params,
      });
      return data;
    },
    queryKey: ['dashboard-tickets', params],
    onError: error => {
      toastError(error);
    },
    enabled: !!startDate && !!endDate,
  });

  const handleNextPrev = (value: NextPrevChartNames) => {
    if (value === NextPrevChartNames.NEXT) {
      setPage(page + 1);
    } else if (value === NextPrevChartNames.PREV) {
      setPage(page - 1);
    }
  };

  useEffect(() => {
    if (selectedData === null && currentData.length === 0) return;
    setIsCurrentData(true);
  }, [currentData, selectedData]);

  useEffect(() => {
    setPage(1);
    setSelectedData(null);
    setCurrentData([]);
    setIsCurrentData(false);
  }, [startDate, endDate]);

  return (
    <DashboardBlock
      title={t('applications')}
      actions={
        <div className={styles.actionsWrapper}>
          <div className={styles.orderValuesWrapper}>
            {isCurrentData && (
              <>
                <Button
                  text={t('back')}
                  type="button"
                  onClick={handleReset}
                  className={styles.backBtn}
                />
                <span className={styles.selectedData}>{selectedData}</span>
              </>
            )}
            <span className={styles.orderTitle}>{t('total_requests')}: </span>
            {ticketData ? (
              <span className={styles.orderValue}>{ticketData?.all_count}</span>
            ) : (
              <SpinnerCircle />
            )}
          </div>
          <NextPrevDataLoadBtn
            isLoading={isLoading}
            isCurrentData={isCurrentData}
            page={page}
            totalPages={ticketData?.total_pages}
            onClick={handleNextPrev}
          />
        </div>
      }
    >
      {ticketData && !isLoading ? (
        <div className={`${styles.chartContent} ${isLoading ? styles.chartContentLoading : ''}`}>
          <div className={styles.fadeIn} key={isCurrentData ? 'current' : `general-${page}`}>
            {isCurrentData === false ? (
              <GeneralApplicationBar
                orderData={ticketData}
                setSelectedData={setSelectedData}
                setCurrentData={setCurrentData}
              />
            ) : (
              <CurrentApplicationBar currentData={currentData} />
            )}
          </div>
        </div>
      ) : (
        <div className={styles.shimmerChart}>
          <div className={styles.shimmerLegend}>
            <div className={styles.shimmerPill} />
            <div className={styles.shimmerPill} />
            <div className={styles.shimmerPill} />
          </div>
          <div className={styles.shimmerBars}>
            {[50, 70, 40, 60, 80].map((h, i) => (
              <div key={i} className={styles.shimmerBar} style={{ height: `${h}%`, animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        </div>
      )}
    </DashboardBlock>
  );
};

export default ApplicationsBarChart;
